import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon, SlidersHorizontalIcon } from 'lucide-react';
import { CalculatorSlider } from '../CalculatorSlider';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
function formatEuro(value: number) {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(value);
}
export function SlideCampaignCostCalculator({}: SlideProps) {
  const [brands, setBrands] = useState(1);
  const [campaignsPerYear, setCampaignsPerYear] = useState(4);
  const [countries, setCountries] = useState(1);
  const [economicsOpen, setEconomicsOpen] = useState(false);
  const [annualCostVisible, setAnnualCostVisible] = useState(false);
  const calculation = useMemo(() => {
    const masterBase = 2900;
    const masterFloor = 1900;
    const followUpMaster = 1000;
    const localizationBase = 999;
    const monthlyInsightsBase = 99;
    const campaignDuration = 3;
    const annualCampaigns = brands * campaignsPerYear;
    const masterProgress = Math.min(1, Math.max(0, (annualCampaigns - 1) / 99));
    const masterCost = masterBase - (masterBase - masterFloor) * masterProgress;
    const countryDiscount = Math.min(0.5, Math.max(0, (countries - 2) / 48 * 0.5));
    const localizationCost = localizationBase * (1 - countryDiscount);
    const insightsDiscount = Math.min(0.5, Math.max(0, (countries - 2) / 48 * 0.5));
    const monthlyInsights = monthlyInsightsBase * (1 - insightsDiscount);
    const perCountryActivation = localizationCost + monthlyInsights * campaignDuration;
    const firstCampaign = masterCost + perCountryActivation * countries;
    const followUpCampaign = followUpMaster + perCountryActivation * countries;
    const totalAnnualCost = brands * firstCampaign + Math.max(0, annualCampaigns - brands) * followUpCampaign;
    const totalDeployments = annualCampaigns * countries;
    return {
      masterCost,
      localizationCost,
      monthlyInsights,
      annualCampaigns,
      totalDeployments,
      totalAnnualCost,
      costPerDeployment: totalDeployments ? totalAnnualCost / totalDeployments : 0
    };
  }, [brands, campaignsPerYear, countries]);
  return <InteriorSlide surface data-id="element-120" data-mp="xtizrdb98zt8">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(1rem,1.8vw,1.6rem)] flex flex-shrink-0 items-end justify-between gap-8" data-id="element-121" data-mp="xuuci2mohhwh">
        <div data-id="element-122" data-mp="17tte51910d9z">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-123" data-mp="1bttqmgckeeee">
            Portfolio economics
          </p>
          <h2 className="max-w-4xl text-[clamp(2rem,3.4vw,3.25rem)] font-extrabold leading-[1.03] tracking-[-0.04em] text-[var(--fg)]" data-id="element-124" data-mp="1gqkhlypwquel">
            See for yourself—move the sliders to see{' '}
            <span className="text-[var(--brand)]" data-id="element-125" data-mp="3ik9zemis0mgu">your potential.</span>
          </h2>
        </div>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-126" data-mp="3b2bqok8f5feo">
        <motion.section initial={{
        opacity: 0,
        x: -18
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.15,
        duration: 0.55
      }} className="flex min-h-0 flex-col gap-4 lg:col-span-5" aria-label="Portfolio calculator inputs" data-id="element-127" data-mp="368ha3po41gjt">
          <article className="rounded-3xl border border-[var(--brand)]/10 bg-white p-5 shadow-sm" data-id="element-128" data-mp="3tylfa1gy5ocx">
            <div className="mb-5 flex items-center gap-3" data-id="element-129" data-mp="3p132fzzt6ozn">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--brand)]/10 text-[var(--brand)]" data-id="element-130" data-mp="3k0ffbpepasap">
                <SlidersHorizontalIcon className="h-5 w-5" aria-hidden="true" data-id="element-131" data-mp="3fz26r7k84b03" />
              </span>
              <div data-id="element-132" data-mp="28nl9ogp5yxz3">
                <h3 className="font-extrabold text-[var(--fg)]" data-id="element-133" data-mp="28jp41rvf7aa8">Portfolio scale</h3>
                <p className="text-xs text-[var(--muted)]" data-id="element-134" data-mp="24j58utueqgqq">Revenue updates instantly.</p>
              </div>
            </div>
            <div className="space-y-5" data-id="element-135" data-mp="12gr1cf6h6bq">
              <CalculatorSlider label="Number of Countries" value={countries} min={1} max={130} onChange={setCountries} data-id="element-136" data-mp="13iekgddrlac" />
              <CalculatorSlider label="Frequency of campaigns annually" value={campaignsPerYear} min={1} max={12} onChange={setCampaignsPerYear} data-id="element-137" data-mp="3ikjiujclqf78" />
              <CalculatorSlider label="Number of Brands" value={brands} min={1} max={40} onChange={setBrands} data-id="element-138" data-mp="f2htgwb3cltb" />
            </div>
          </article>

          <article className="overflow-hidden rounded-2xl border border-[var(--brand)]/10 bg-white" data-id="element-139" data-mp="k0t3b71dywbj">
            <button type="button" onClick={() => setEconomicsOpen(open => !open)} aria-expanded={economicsOpen} className="flex w-full items-center gap-3 px-5 py-3 text-left transition hover:bg-[var(--brand)]/[0.03]" data-id="element-140" data-mp="610czctrui43">
              <div className="h-5 w-5 text-[var(--brand)]" aria-hidden="true" data-id="element-141" data-mp="2qtvjncuhnjw7" />
              <span className="flex-1" data-id="element-142" data-mp="2uuxgxo4uhlvj">
                <span className="block text-sm font-bold text-[var(--fg)]" data-id="element-143" data-mp="2n9la4psuwrrq">Unit economics</span>
                <span className="mt-0.5 block text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--muted)]" data-id="element-144" data-mp="2ias75rb74j5t">Discounted for scale</span>
              </span>
              <ChevronDownIcon className={`h-4 w-4 text-[var(--muted)] transition ${economicsOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-145" data-mp="2x79gye3qlpq9" />
            </button>
            <AnimatePresence initial={false} data-id="element-146" data-mp="24ikadzjwws74">
              {economicsOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="overflow-hidden" data-id="element-147" data-mp="1zkaj401b6bqs">
                  <div className="grid grid-cols-3 gap-2 border-t border-[var(--brand)]/10 bg-[var(--brand)]/[0.03] p-3" data-id="element-148" data-mp="29glw6s8q35ma">
                    <Rate label="Master Snak" value={formatEuro(calculation.masterCost)} data-id="element-149" data-mp="1gu12hl4ryhfk" />
                    <Rate label="Localization" value={formatEuro(calculation.localizationCost)} data-id="element-150" data-mp="26hwwp8ctcetz" />
                    <Rate label="Insights / month" value={formatEuro(calculation.monthlyInsights)} data-id="element-151" data-mp="2ajiaqx132z5r" />
                  </div>
                </motion.div>}
            </AnimatePresence>
          </article>
        </motion.section>

        <motion.section initial={{
        opacity: 0,
        x: 18
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.27,
        duration: 0.55
      }} className="flex min-h-0 flex-col gap-4 lg:col-span-7" aria-label="Portfolio calculator results" data-id="element-152" data-mp="2k9l3qx87gd43">
          <article className="relative overflow-hidden rounded-3xl bg-[var(--brand)] p-[clamp(1.5rem,2.5vw,2.5rem)] text-white shadow-lg" data-id="element-153" data-mp="1wjhddrk129v1">
            <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full border-[34px] border-white/10" data-id="element-154" data-mp="21k52u8slyin5" />
            <p className="relative text-xs font-bold uppercase tracking-[0.16em] text-white/65" data-id="element-155" data-mp="3810bx6mnxon9">
              Cost per localized campaign
            </p>
            <motion.p key={calculation.costPerDeployment} initial={{
            opacity: 0.6,
            y: 4
          }} animate={{
            opacity: 1,
            y: 0
          }} className="relative mt-3 text-[clamp(3.25rem,7vw,6.5rem)] font-extrabold leading-none tracking-[-0.06em]" data-id="element-156" data-mp="11ax22difqaq9">
              {formatEuro(calculation.costPerDeployment)}
            </motion.p>
            <p className="relative mt-3 text-sm text-white/70" data-id="element-157" data-mp="ijvytdaa7t0w">Per localized deployment</p>
          </article>

          <div className="grid grid-cols-2 gap-4" data-id="element-158" data-mp="dkt7swr4znbx">
            <ResultCard value={calculation.annualCampaigns.toLocaleString()} label="Master campaigns / year" data-id="element-159" data-mp="dn5mn11llhkr" />
            <ResultCard value={calculation.totalDeployments.toLocaleString()} label="Localized deployments / year" accent data-id="element-160" data-mp="8nt06hen2cip" />
          </div>

          <article className="flex flex-1 items-center justify-between gap-6 rounded-3xl border border-[var(--brand)]/10 bg-white p-5 shadow-sm" data-id="element-161" data-mp="23094vdrsdilg">
            <div data-id="element-162" data-mp="1xytgpcubavyu">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--muted)]" data-id="element-163" data-mp="16vk2dxlz1u8m">
                Estimated annual cost
              </p>
              <p className="mt-2 text-sm leading-relaxed text-[var(--muted)]" data-id="element-164" data-mp="1bu54y3op4q08">
                Land with a pilot brand in a few countries, then expand across the portfolio.
              </p>
            </div>
            <div className="flex-shrink-0 text-right" data-id="element-165" data-mp="1230qr66uh96g">
              <AnimatePresence mode="wait" data-id="element-166" data-mp="o4j6xjk28gtt">
                {annualCostVisible ? <motion.p key="cost" initial={{
                opacity: 0,
                y: 5
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -5
              }} className="text-3xl font-extrabold text-[var(--brand)]" data-id="element-167" data-mp="o7f435o331h7">
                    {formatEuro(calculation.totalAnnualCost)}
                  </motion.p> : <motion.p key="hidden" initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} className="text-lg font-bold text-[var(--muted)]" data-id="element-168" data-mp="29gknz9wrtvpw">
                    Reveal total
                  </motion.p>}
              </AnimatePresence>
              <button type="button" onClick={() => setAnnualCostVisible(visible => !visible)} aria-pressed={annualCostVisible} className="mt-2 rounded-full bg-[var(--brand)]/10 px-4 py-2 text-xs font-bold text-[var(--brand)] transition hover:bg-[var(--brand)]/15" data-id="element-169" data-mp="3tymrj2ure2cp">
                {annualCostVisible ? 'Hide annual cost' : 'Show annual cost'}
              </button>
            </div>
          </article>
        </motion.section>
      </div>
    </InteriorSlide>;
}
function Rate({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div className="rounded-xl bg-white p-3 text-center" data-id="element-170" data-mp="plzpgixp1od7">
      <p className="text-lg font-extrabold text-[var(--brand)]" data-id="element-171" data-mp="pkew2wn48bgx">{value}</p>
      <p className="mt-1 text-[9px] font-bold uppercase tracking-wide text-[var(--muted)]" data-id="element-172" data-mp="2wd7hd0gbg1p1">{label}</p>
    </div>;
}
function ResultCard({
  value,
  label,
  accent = false
}: {
  value: string;
  label: string;
  accent?: boolean;
}) {
  return <article className="rounded-2xl border border-[var(--brand)]/10 bg-white p-5 shadow-sm" data-id="element-173" data-mp="2c348db61wv5d">
      <p className={`text-3xl font-extrabold ${accent ? 'text-[var(--brand)]' : 'text-[var(--fg)]'}`} data-id="element-174" data-mp="281iy7xkwiuyv">{value}</p>
      <p className="mt-2 text-xs font-semibold text-[var(--muted)]" data-id="element-175" data-mp="232pkq55m73yp">{label}</p>
    </article>;
}